/**
 * Google Consent Mode v2 helpers.
 *
 * Visitors in the EEA, UK and Switzerland start with ad and analytics
 * storage denied until they answer the cookie banner; everyone else starts
 * granted. The default is pushed to the dataLayer BEFORE Google Tag Manager
 * loads (see AnalyticsLoader), which is what lets GTM/GA4/Google Ads honour
 * the choice. The visitor's answer is kept in localStorage for 12 months.
 */

export const CONSENT_STORAGE_KEY = "valor_consent_v2";
export const CONSENT_EVENT = "valor:consent-change";
export const CONSENT_OPEN_EVENT = "valor:consent-open";
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

const CONSENT_TYPES = [
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "analytics_storage",
];

// IANA zones for the EEA (EU + Iceland, Liechtenstein, Norway), the UK and
// Switzerland. Used only as a client-side fallback for region detection.
const REGULATED_TZ_PREFIXES = [
  "Europe/Amsterdam", "Europe/Andorra", "Europe/Athens", "Europe/Belfast", "Europe/Belgrade",
  "Europe/Berlin", "Europe/Bratislava", "Europe/Brussels", "Europe/Bucharest", "Europe/Budapest",
  "Europe/Busingen", "Europe/Copenhagen", "Europe/Dublin", "Europe/Gibraltar", "Europe/Guernsey",
  "Europe/Helsinki", "Europe/Isle_of_Man", "Europe/Jersey", "Europe/Lisbon", "Europe/Ljubljana",
  "Europe/London", "Europe/Luxembourg", "Europe/Madrid", "Europe/Malta", "Europe/Mariehamn",
  "Europe/Monaco", "Europe/Oslo", "Europe/Paris", "Europe/Prague", "Europe/Riga", "Europe/Rome",
  "Europe/San_Marino", "Europe/Sofia", "Europe/Stockholm", "Europe/Tallinn", "Europe/Vaduz",
  "Europe/Vatican", "Europe/Vienna", "Europe/Vilnius", "Europe/Warsaw", "Europe/Zagreb",
  "Europe/Zurich", "Atlantic/Azores", "Atlantic/Canary", "Atlantic/Faroe", "Atlantic/Madeira",
  "Atlantic/Reykjavik", "Arctic/Longyearbyen",
];

const REGULATED_LOCALE_REGIONS = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV",
  "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "IS", "LI", "NO", "GB", "CH",
]);

function readRegionHint() {
  if (typeof document === "undefined") return null;
  // Hosting can stamp a country code onto the document (e.g. from a geo
  // header) as <html data-country="DE">; honour it when present.
  const fromHtml = document.documentElement.getAttribute("data-country");
  if (fromHtml) return fromHtml.toUpperCase();
  const meta = document.querySelector('meta[name="visitor-country"]');
  return meta?.content ? meta.content.toUpperCase() : null;
}

export function isRegulatedRegion() {
  const hint = readRegionHint();
  if (hint) return REGULATED_LOCALE_REGIONS.has(hint);
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (REGULATED_TZ_PREFIXES.includes(tz)) return true;
  } catch { /* ignore */ }
  try {
    const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const l of locales) {
      const region = (l || "").split("-")[1];
      if (region && REGULATED_LOCALE_REGIONS.has(region.toUpperCase())) return true;
    }
  } catch { /* ignore */ }
  return false;
}

export function readStoredConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.granted !== "boolean" || !parsed.at) return null;
    if (Date.now() - parsed.at > CONSENT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function gtag() {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function consentState(granted) {
  const value = granted ? "granted" : "denied";
  return Object.fromEntries(CONSENT_TYPES.map((t) => [t, value]));
}

/**
 * Push the consent default. Must run before gtm.js is requested.
 * Returns true when the banner should be shown.
 */
export function applyConsentDefault() {
  if (typeof window === "undefined") return false;
  if (window.__valor_consent_default_applied) return window.__valor_consent_needs_banner === true;
  window.__valor_consent_default_applied = true;

  const regulated = isRegulatedRegion();
  const stored = readStoredConsent();

  if (!regulated) {
    gtag("consent", "default", consentState(true));
    window.__valor_consent_needs_banner = false;
    return false;
  }

  gtag("consent", "default", { ...consentState(false), wait_for_update: 500 });
  if (stored) {
    gtag("consent", "update", consentState(stored.granted));
    window.__valor_consent_needs_banner = false;
    return false;
  }
  window.__valor_consent_needs_banner = true;
  return true;
}

export function recordConsent(granted) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({ granted, at: Date.now(), version: 2 })
    );
  } catch { /* storage unavailable; the session still gets the update */ }
  gtag("consent", "update", consentState(granted));
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: granted ? "consent_granted" : "consent_denied" });
  window.__valor_consent_needs_banner = false;
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { granted } }));
}

export function openConsentSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}
